import { ArrowBackIos as ArrowBackIosIcon, ArrowForwardIos as ArrowForwardIosIcon } from '@mui/icons-material/';
import { Grid } from '@mui/material';
import { RoutalPalette } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import styled from 'styled-components';
import { useRetrieveActivePlanDrivers } from '../../../../../hooks/plan/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanStops } from '../../../../../hooks/plan/useRetrieveActivePlanStops';
import { useSetActivePlanSelectedStops } from '../../../../../hooks/plan/useSetActivePlanSelectedStops';
import { useUpdateDriverStops } from '../../../../../hooks/plan/useUpdateDriverStops';
import { DriverPanel } from './DriverPanel';

const CollapseColumnsButton = styled.div`
  position: absolute;
  width: 24px;
  height: 24px;
  border-radius: 2px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  background-color: ${RoutalPalette.white};
  right: -25px;
  top: 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  color: ${RoutalPalette.grey1};
  border-right: 1px solid ${RoutalPalette.grey3};
  border-bottom: 1px solid ${RoutalPalette.grey3};
  cursor: pointer;
  z-index: 451;

  :hover {
    background-color: ${RoutalPalette.grey5};
  }
`;

export const driverColumnUnassigned = `unassigned`;
export type DriverColumnUnassignedTypes = `unassigned`;

export interface DriverColumn {
  tab_index: number;
  driver_id: string | DriverColumnUnassignedTypes;
}

const PlanEditTabsContainer = styled(Grid)`
  position: relative;
  height: calc(100vh - 64px);
  flex-wrap: nowrap !important;
  max-width: ${380 * 2}px;
  // width: fit-content;
`;

export const PlanEditTabs = () => {
  const [driverColumns, setDriverColumns] = useState<DriverColumn[]>([]);
  const { map: driversMap, data: drivers } = useRetrieveActivePlanDrivers();
  const { map: stopsMap, data: stops } = useRetrieveActivePlanStops();

  const { selectedStops: selectedStopsIds, setSelectedStops: selectStops } = useSetActivePlanSelectedStops();
  const { run: updateDriverStops } = useUpdateDriverStops();

  useEffect(() => {
    if (driverColumns.length === 0) {
      if ((drivers ?? []).length) {
        setDriverColumns([{ driver_id: drivers![0].id, tab_index: 0 }]);
      } else {
        setDriverColumns([{ driver_id: driverColumnUnassigned, tab_index: 0 }]);
      }
    }
  }, [drivers, driverColumns]);

  // remove last element of driverColumns
  const removeDriverColumn = () => {
    const newDriverColumns = [...driverColumns];
    if (newDriverColumns.length > 1) {
      newDriverColumns.pop();
      setDriverColumns(newDriverColumns);
    }
  };

  const addDriverColumn = () => {
    if (driverColumns.length < drivers!.length + 1) {
      const unusedDrivers = drivers!.filter(
        (driver) => !driverColumns.some((driverColumn) => driverColumn.driver_id === driver.id)
      );
      const newDriver = unusedDrivers.length ? unusedDrivers[0] : undefined;
      const newDriverId = unusedDrivers.length ? unusedDrivers[0].id : driverColumnUnassigned;
      const newStops = newDriver ? newDriver.stops : stops!.filter((stop) => !stop.driver_id);
      const newDriverColumns = [
        ...driverColumns,
        { driver_id: newDriverId, stops: newStops, tab_index: driverColumns.length },
      ];
      setDriverColumns(newDriverColumns);
    }
  };

  const onDriverChange = (driverId: string, index: number) => {
    const newDriverColumns = [...driverColumns];
    newDriverColumns[index].driver_id = driverId;
    setDriverColumns(newDriverColumns);
  };

  return (
    <PlanEditTabsContainer wrap="nowrap" container direction="row" style={{ width: `${380 * driverColumns.length}px` }}>
      <DragDropContext
        onDragStart={(initial) => {
          if (!selectedStopsIds.includes(initial.draggableId)) {
            selectStops([initial.draggableId]);
          }
        }}
        onDragEnd={({ draggableId: stopId, source, destination }) => {
          if (destination) {
            const { droppableId: driverIdTo, index: indexTo } = destination;
            const selectedStops = selectedStopsIds
              .map((stopId) => stopsMap[stopId])
              .filter((stop) => stop !== undefined);
            updateDriverStops({ driver: driversMap[driverIdTo], stops: selectedStops, index: indexTo });
          }
        }}
      >
        {driverColumns.map((driverColumn, idx) => (
          <DriverPanel
            key={`driver-column-${driverColumn.driver_id}-${idx}`}
            driverColumnsIds={driverColumns.map((driverColumn) => driverColumn.driver_id)}
            driverColumn={driverColumn}
            onDriverChange={(newDriverId) => onDriverChange(newDriverId, idx)}
          />
        ))}
      </DragDropContext>

      {(drivers ?? []).length > 0 ? (
        <Grid>
          {driverColumns.length === 1 ? (
            <CollapseColumnsButton onClick={addDriverColumn}>
              <ArrowForwardIosIcon style={{ marginLeft: `1px`, fontSize: `14px` }} />
            </CollapseColumnsButton>
          ) : null}
          {driverColumns.length === 2 ? (
            <CollapseColumnsButton onClick={removeDriverColumn}>
              <ArrowBackIosIcon style={{ marginLeft: `4px`, fontSize: `14px` }} />
            </CollapseColumnsButton>
          ) : null}
        </Grid>
      ) : null}
    </PlanEditTabsContainer>
  );
};
